import { snippetz, type HarRequest } from '@scalar/snippetz';
import { HIGHLIGHT_LANGUAGES, PLAIN_TEXT_LANGUAGE } from '../../util/highlight-languages';
import { isObject, type JsonObject } from './openapi-utils';

const generator = snippetz();

/** Client ids are "target/client", e.g. "shell/curl" */
export const DEFAULT_SNIPPET_CLIENT = 'shell/curl';

/** Every client snippetz supports, grouped by language for a Select */
export const SNIPPET_CLIENT_OPTIONS = generator.clients().map((target) => ({
  group: target.title,
  items: target.clients.map((plugin) => ({
    value: `${target.key}/${plugin.client}`,
    label: `${target.title} - ${plugin.title}`,
  })),
}));

/** Shiki language for each snippetz target, where the two names differ */
const TARGET_LANGUAGES: Record<string, string> = {
  js: 'javascript',
  node: 'javascript',
  shell: 'bash',
};

/** The language to highlight a client's snippet as, or plain text when that grammar is not loaded */
export function getSnippetLanguage(clientId: string): string {
  const [target] = clientId.split('/');
  const language = TARGET_LANGUAGES[target] ?? target;
  return HIGHLIGHT_LANGUAGES.includes(language) ? language : PLAIN_TEXT_LANGUAGE;
}

/** The first server url in the document, without a trailing slash */
export function getServerUrl(spec: JsonObject): string {
  const server = Array.isArray(spec.servers) ? spec.servers[0] : undefined;
  const url = isObject(server) && typeof server.url === 'string' ? server.url : '';
  return url.replace(/\/+$/, '');
}

/** Prints a JSON request in the chosen client's language, e.g. as a curl command */
export function printSnippet(
  clientId: string,
  request: { method: string; url: string; body: unknown },
): string | undefined {
  const [target, client] = clientId.split('/');
  const plugin = generator.findPlugin(target, client);
  if (!plugin) return undefined;

  const harRequest: Partial<HarRequest> = {
    method: request.method.toUpperCase(),
    url: request.url,
    headers: [{ name: 'Content-Type', value: 'application/json' }],
    postData: { mimeType: 'application/json', text: JSON.stringify(request.body, null, 2) },
  };
  return plugin.generate(harRequest);
}
